import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    background-color: ${theme.colors.background};
    color-scheme: light dark;
  }

  body {
    font-family: 'Inter var', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
      'Noto Sans', sans-serif;
    background-color: ${theme.colors.background};
    color: ${theme.colors.text};
    line-height: ${theme.typography.lineHeights.normal};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
    min-height: 100vh;
  }

  /* Modern Reset */
  img, picture, video, canvas, svg {
    display: block;
    max-width: 100%;
  }

  input, button, textarea, select {
    font: inherit;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    color: ${theme.colors.text};
    font-weight: ${theme.typography.fontWeights.bold};
    line-height: ${theme.typography.lineHeights.tight};
    margin-bottom: ${theme.spacing.md};
    letter-spacing: ${theme.typography.letterSpacing.tight};
  }

  h1 {
    font-size: clamp(${theme.typography.fontSizes['4xl']}, 5vw, ${theme.typography.fontSizes['6xl']});
    font-weight: ${theme.typography.fontWeights.black};
    background: ${theme.colors.gradient.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  h2 {
    font-size: clamp(${theme.typography.fontSizes['3xl']}, 4vw, ${theme.typography.fontSizes['5xl']});
  }

  h3 {
    font-size: clamp(${theme.typography.fontSizes['2xl']}, 3vw, ${theme.typography.fontSizes['4xl']});
  }

  h4 {
    font-size: ${theme.typography.fontSizes['2xl']};
  }

  h5 {
    font-size: ${theme.typography.fontSizes.xl};
  }

  h6 {
    font-size: ${theme.typography.fontSizes.lg};
  }

  p {
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.textSecondary};
    font-size: clamp(${theme.typography.fontSizes.base}, 2vw, ${theme.typography.fontSizes.lg});
    max-width: 65ch;
  }

  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    transition: all ${theme.transitions.fast};
    position: relative;
    font-weight: ${theme.typography.fontWeights.medium};

    &:hover {
      color: ${theme.colors.primaryHover};
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
      border-radius: ${theme.borderRadius.sm};
    }

    &.underline {
      &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 100%;
        height: 2px;
        background: ${theme.colors.gradient.primary};
        transform: scaleX(0);
        transform-origin: right;
        transition: transform ${theme.transitions.fast};
      }

      &:hover::after {
        transform: scaleX(1);
        transform-origin: left;
      }
    }
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    font-weight: ${theme.typography.fontWeights.medium};
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  /* Layout */
  .container {
    width: 100%;
    max-width: min(1200px, 90%);
    margin: 0 auto;
    padding: 0 ${theme.spacing.md};
  }

  /* Utility Classes */
  .glass {
    background: ${theme.colors.glass.light};
    backdrop-filter: blur(${theme.colors.glass.blur});
    -webkit-backdrop-filter: blur(${theme.colors.glass.blur});
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: ${theme.shadows.colored};
  }

  .glass-dark {
    background: ${theme.colors.glass.dark};
    backdrop-filter: blur(${theme.colors.glass.blur});
    -webkit-backdrop-filter: blur(${theme.colors.glass.blur});
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  .gradient-text {
    background: ${theme.colors.gradient.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .gradient-border {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 2px;
      background: ${theme.colors.gradient.primary};
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
    }
  }

  /* Animations */
  @keyframes float {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .animate-float {
    animation: float 3s ease-in-out infinite;
  }

  .animate-pulse {
    animation: pulse 2s ease-in-out infinite;
  }

  .animate-shimmer {
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.08) 50%,
      transparent 100%
    );
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 0px;
    height: 8px;
  }

  // ::-webkit-scrollbar-track {
  //   background: ${theme.colors.backgroundAlt};
  // }

  // ::-webkit-scrollbar-thumb {
  //   background: ${theme.colors.primary};
  //   border-radius: ${theme.borderRadius.full};
    
  //   &:hover {
  //     background: ${theme.colors.primaryHover};
  //   }
  // }

  /* Selection */
  ::selection {
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
  }

  /* Dark Mode */
  @media (prefers-color-scheme: dark) {
    body {
      background-color: ${theme.colors.dark.background};
      color: ${theme.colors.dark.text};
    }

    h1, h2, h3, h4, h5, h6 {
      color: ${theme.colors.dark.text};
    }

    p {
      color: ${theme.colors.dark.textSecondary};
    }

    .glass {
      background: ${theme.colors.glass.dark};
      border-color: rgba(255, 255, 255, 0.08);
    }
  }

  /* Responsive Typography */
  @media (max-width: ${theme.breakpoints.md}) {
    html {
      font-size: 14px;
    }
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    html {
      font-size: 12px;
    }
  }
`; 